import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import type { Course } from "@/types";

interface Props {
  courses: Course[];
  current: Course | null;
  /** Ouvre la gestion des cours (créer / dupliquer / archiver / roster). */
  onManage: () => void;
}

/** Couleurs de cours autorisées → variable CSS du design system. */
export const COURSE_COLORS = ["blue", "green", "mauve", "peach", "teal", "red", "yellow"];
export const colorVar = (c: string) =>
  COURSE_COLORS.includes(c) ? `var(--${c === "mauve" ? "purple" : c === "peach" ? "orange" : c})` : "var(--blue)";

/** Barre de contexte persistante : indique en permanence le cours actif (nom +
 *  couleur), permet d'en changer (navigation par URL → onglets isolés), affiche le
 *  code d'inscription et un raccourci roster. Un cours sans élève = « mode test ». */
export default function CourseContextBar({ courses, current, onManage }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!current) return null;
  const accent = colorVar(current.color);
  const isTest = current.student_count === 0;

  const copyCode = () => {
    navigator.clipboard?.writeText(current.join_code).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div style={{ ...styles.bar, ...(isTest ? styles.barTest : { borderBottom: `2px solid ${accent}` }) }}>
      {/* Sélecteur de cours */}
      <div ref={ref} style={{ position: "relative" }}>
        <button style={styles.selector} onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
          <span style={{ ...styles.dot, background: accent }} />
          <span style={styles.courseName}>{current.title}</span>
          <Icon name={open ? "expanded" : "collapsed"} size={14} style={{ color: "var(--text-muted)" }} />
        </button>
        {open && (
          <div style={styles.menu} role="menu">
            {courses.map((c) => (
              <button
                key={c.id}
                style={{ ...styles.menuItem, ...(c.id === current.id ? { background: "var(--bg-base)" } : {}) }}
                onClick={() => { setOpen(false); navigate(`/instructor/c/${c.id}`); }}
              >
                <span style={{ ...styles.dot, background: colorVar(c.color) }} />
                <span style={styles.menuItemName}>{c.title}</span>
                {c.archived && <span style={styles.archivedTag}>archivé</span>}
              </button>
            ))}
            <div style={styles.menuSep} />
            <button style={styles.menuItem} onClick={() => { setOpen(false); onManage(); }}>
              <Icon name="panel" size={14} /> Gérer mes cours…
            </button>
          </div>
        )}
      </div>

      {isTest && (
        <span style={styles.testTag} title="Aucun élève inscrit — espace de test">
          <Icon name="alert" size={13} style={{ color: "var(--text-muted)" }} /> Test — aucun élève
        </span>
      )}

      <span style={{ flex: 1 }} />

      {/* Code d'inscription (projetable) + roster */}
      <span style={styles.codeLabel}>Code</span>
      <button style={{ ...styles.codeBtn, borderColor: accent, color: accent }} onClick={copyCode} title="Copier le code d'inscription">
        <span style={styles.code}>{current.join_code}</span>
        <Icon name={copied ? "done" : "duplicate"} size={13} />
      </button>
      <button style={styles.rosterBtn} onClick={onManage} title="Élèves du cours">
        <Icon name="run" size={13} style={{ display: "none" }} />
        {current.student_count} élève{current.student_count > 1 ? "s" : ""}
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    display: "flex", alignItems: "center", gap: 12, padding: "6px 16px",
    background: "var(--bg-bar)", borderBottom: "1px solid var(--border-soft)", flexShrink: 0,
    position: "sticky", top: 0, zIndex: 20,
  },
  barTest: { background: "var(--bg-base)", borderBottom: "2px dashed var(--border)" },
  selector: {
    display: "inline-flex", alignItems: "center", gap: 8, background: "transparent",
    border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "4px 10px",
    cursor: "pointer", color: "var(--text)", fontSize: 14, fontWeight: 600,
  },
  dot: { width: 12, height: 12, borderRadius: "50%", flexShrink: 0 },
  courseName: { maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  menu: {
    position: "absolute", top: "calc(100% + 6px)", left: 0, minWidth: 240, zIndex: 30,
    background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)",
    boxShadow: "var(--shadow-1)", padding: 4, display: "flex", flexDirection: "column", gap: 2,
  },
  menuItem: {
    display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
    background: "transparent", border: "none", borderRadius: "var(--radius-sm)", color: "var(--text)",
    padding: "8px 10px", cursor: "pointer", fontSize: 13,
  },
  menuItemName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  archivedTag: { fontSize: 10.5, color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 999, padding: "1px 6px" },
  menuSep: { height: 1, background: "var(--border-soft)", margin: "4px 0" },
  testTag: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" },
  codeLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)" },
  codeBtn: {
    display: "inline-flex", alignItems: "center", gap: 8, background: "transparent",
    border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "4px 10px", cursor: "pointer",
  },
  code: { fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 15, letterSpacing: 1 },
  rosterBtn: {
    background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)",
    color: "var(--text-muted)", padding: "4px 10px", cursor: "pointer", fontSize: 13, whiteSpace: "nowrap",
  },
};
